// Klucze firmy — szkielet klienta API (poziom 2 hierarchii, docs/model-kluczy-firmowych.md).
//
// STAN NA F1: to jest SAM KONTRAKT, bez ani jednego konsumenta (tak przewiduje plan fazy).
// Endpointy powstają w F1/F2 po stronie BE (WorkspaceKeysRepository + WorkspaceKeyController).
// Wszystkie zapytania są domyślnie WYŁĄCZONE (enabled=false), więc zaimportowanie tego pliku
// nie generuje ruchu sieciowego ani błędów 404.
//
// Ścieżki są zgodne z sekcją 3 dokumentu („Dołączenie do firmy i wydanie klucza"):
//   GET  /api/workspace/key                    → generacje + granty ZALOGOWANEGO
//   GET  /api/workspace/members/awaiting-key   → aktywni członkowie bez grantu (kierunek ODWRÓCONY)
//   POST /api/workspace/member/key             → wydanie klucza firmy jednemu członkowi
//   POST /api/workspace/key/rotate             → nowa generacja + granty dla wszystkich
//
// I4: klucz prywatny firmy nigdy nie trafia tu w postaci odszyfrowanej. Pola `PrivateKey`
// w żądaniach to ZAWSZE PGP(intermediate.pub odbiorcy){company.priv} w base64.

import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  ICompanyKeysView,
  ICompanyMemberAwaitingKey,
  IGrantCompanyKeyRequest,
  IRotateCompanyKeyRequest,
} from "dto/ICompanyKey";
import { ICreateCompanyKeyPayload } from "utils/buildCompanyKeyPayload";

const queryKeys = {
  getCompanyKeys: "companyKeysService.getCompanyKeys",
  getMembersAwaitingKey: "companyKeysService.getMembersAwaitingKey",
};

const getCompanyKeys = (): Promise<{ data: ICompanyKeysView }> => {
  return axiosInstance.get("/workspace/key");
};

/** Generacje klucza firmy wraz z grantami zalogowanego. @param enabled domyślnie FALSE. */
const useGetCompanyKeys = (enabled: boolean = false) => {
  return useCustomQuery(queryKeys.getCompanyKeys, () => getCompanyKeys(), {
    enabled,
    staleTime: 0,
    cacheTime: 0,
    retry: 0,
  });
};

const getMembersAwaitingKey = (): Promise<{
  data: ICompanyMemberAwaitingKey[];
}> => {
  return axiosInstance.get("/workspace/members/awaiting-key");
};

/**
 * Aktywni członkowie WŁASNEJ firmy bez grantu na aktywną generację — kolejka wydania klucza
 * dla osoby z uprawnieniem workspace.keys.grant. @param enabled domyślnie FALSE.
 */
const useGetMembersAwaitingKey = (enabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getMembersAwaitingKey,
    () => getMembersAwaitingKey(),
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      retry: 0,
    },
  );
};

const grantCompanyKey = (payload: IGrantCompanyKeyRequest) => {
  return axiosInstance.post("/workspace/member/key", payload);
};

/** Wydanie klucza firmy jednemu członkowi. BE waliduje członkostwo, uprawnienie i duplikat. */
const useGrantCompanyKey = () => {
  return useCustomMutation(grantCompanyKey);
};

const rotateCompanyKey = (payload: IRotateCompanyKeyRequest) => {
  return axiosInstance.post("/workspace/key/rotate", payload);
};

/**
 * Nowa generacja klucza firmy + granty dla wszystkich pozostałych aktywnych członków.
 * Q1: rotacja jako funkcja użytkowa jest poza zakresem przebudowy — kontrakt istnieje,
 * bo generacje zostają w schemacie i dołożenie kampanii nie może wymagać drugiej migracji
 * materiału kluczowego.
 */
const useRotateCompanyKey = () => {
  return useCustomMutation(rotateCompanyKey);
};

const createCompanyKey = (payload: ICreateCompanyKeyPayload) => {
  return axiosInstance.post("/workspace/key", payload);
};

/**
 * PIERWSZA generacja klucza dla firmy, która JUŻ ISTNIEJE, ale klucza jeszcze nie ma
 * (POST /api/workspace/key → `WorkspaceKeysRepository.CreateKey`).
 *
 * Odrębna od zakładania firmy: tamta ścieżka wysyła ten sam ładunek w polu `CompanyKey`
 * żądania POST /api/workspace. Ta jest dla firm powstałych ZANIM model kluczy firmowych
 * wszedł — bez niej taka firma zostaje na zawsze bez klucza, a `encryptForCompany` odbija
 * każdy zapis (`CompanyKeyMissingError`), nie mając jak tego naprawić.
 *
 * BE odrzuci żądanie, gdy firma ma już jakąkolwiek generację („od tego jest rotacja") oraz
 * gdy wołający nie ma uprawnienia `workspace.keys.grant`.
 */
const useCreateCompanyKey = () => {
  return useCustomMutation(createCompanyKey);
};

export default {
  queryKeys,
  getCompanyKeys,
  useGetCompanyKeys,
  getMembersAwaitingKey,
  useGetMembersAwaitingKey,
  useGrantCompanyKey,
  useRotateCompanyKey,
  createCompanyKey,
  useCreateCompanyKey,
};
